import React from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Paper,
  useTheme,
} from "@mui/material";
import { Comment as CommentIcon } from "@mui/icons-material";
import { tokens } from "../../../theme";
import type { MemorialComment } from "../../../types/contact";

interface MemorialCommentsCardProps {
  comments: MemorialComment[];
  loading: boolean;
  onStreamClick?: (streamId: number) => void;
}

export const MemorialCommentsCard: React.FC<MemorialCommentsCardProps> = ({
  comments,
  loading,
  onStreamClick,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <Paper sx={{ p: 3, boxShadow: 0 }}>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <CommentIcon />
        <Typography variant="h6" fontWeight="medium">
          Memorial Comments
        </Typography>
        {comments.length > 0 && (
          <Chip label={comments.length} size="small" color="primary" />
        )}
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : comments.length === 0 ? (
        <Typography
          variant="body1"
          sx={{ fontStyle: "italic", textAlign: "center", py: 4 }}
        >
          No memorial comments found for this contact
        </Typography>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: colors.primary[800] }}>
                <TableCell>Comment</TableCell>
                <TableCell>Stream / Event</TableCell>
                <TableCell>Source</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {comments.map((comment, index) => (
                <TableRow
                  key={comment.id}
                  sx={{
                    backgroundColor:
                      index % 2 === 0 ? "white" : colors.primary[800],
                    "&:hover": { backgroundColor: colors.grey[500] },
                    cursor: comment.stream_id ? "pointer" : "default",
                  }}
                  onClick={() => {
                    if (comment.stream_id && onStreamClick) {
                      onStreamClick(comment.stream_id);
                    }
                  }}
                >
                  <TableCell sx={{ maxWidth: 300 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                      }}
                    >
                      {comment.content}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {comment.stream_title || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={comment.comment_source}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={comment.is_approved ? "Approved" : "Pending"}
                      size="small"
                      color={comment.is_approved ? "success" : "warning"}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(comment.created_at)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

export default MemorialCommentsCard;
